import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { FormController } from '@adornis/forms/x-form-controller.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import type { ProductLKK } from '@chancenwerk/_cms/products/entities/ProductLKK.js';
import '@chancenwerk/components/chancen-component-choose-lkk-days.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { KPF } from '../../../../src/public-access/api/KPFPublicInformationAPI.js';
import { AnmeldungPageRegistrationBase } from './anmeldung-component-formular-base.js';

/**
 * @customElement anmeldung-component-formular-lkk-us
 */
@customElement('anmeldung-component-formular-lkk-us')
export class AnmeldungPageRegistrationLKKUS extends AnmeldungPageRegistrationBase {
  @property({ attribute: false }) form!: FormController<Student>;
  @property({ attribute: false }) translation!: TranslationController<any>;
  @property({ attribute: false }) student!: RXController<Student>;
  @property({ attribute: false }) choosenKPF!: RXController<KPF | undefined>;
  @property({ attribute: 'school-level' }) schoolLevel: 'us' | 'os' = 'us';

  private _emitStudentChanged() {
    const event = new CustomEvent('value-picked', {
      detail: {
        value: this.student.value,
      },
    });
    this.dispatchEvent(event);
  }

  override render() {
    let choices: TemplateResult;
    if (((this.choosenKPF.value?.produkt as ProductLKK).durchfuehrungsTage ?? []).length === 0) {
      choices = html`<cr-basic-card ${css({ boxShadow: 'none', background: this.colors.tone.error.shade(100) })}>
        <cr-text>Leider stehen aktuell keine Termine zur Verfügung</cr-text>
      </cr-basic-card>`;
    } else {
      choices = html`<cr-flex horizontal space-between crossaxis-center>
        <chancen-component-choose-lkk-days
          .kpf=${this.choosenKPF.value}
          .student=${this.student.value}
          @value-picked=${(e: ValueEvent<Student>) => {
            this.student.next(e.detail.value);
            this._emitStudentChanged();
          }}
        >
        </chancen-component-choose-lkk-days>
        ${this.schoolLevel === 'us'
          ? html`
              <cr-icon
                end
                ${css({
                  padding: this.spacing.xs,
                  color: this.colors.accent.shade(500),
                  cursor: 'pointer',
                })}
              >
                info
                <cr-tooltip clickable>
                  <cr-text ${css({ maxWidth: '400px' })}>
                    ${this.translation.translate('tooltips', 'further_instructions_days_available')}
                  </cr-text>
                </x-tooltip>
              </cr-icon>
            `
          : nothing}
      </cr-flex>`;
    }

    return html`<cr-flex space="md">
      ${this.schoolLevel === 'us'
        ? html` <cr-text bold> ${this.translation.translate('step3', this.schoolLevel, 'lkkDays')} </cr-text> `
        : nothing}
      ${this.schoolLevel === 'os'
        ? html`<cr-flex horizontal crossaxis-center space="md">
            <cr-h4> Ich helfe in der Lernförderung: </cr-h4>
            <cr-icon>
              info
              <cr-tooltip clickable>
                <cr-text> ${this.translation.translate('step3', this.schoolLevel, 'lkkDays')} </cr-text>
              </cr-tooltip>
            </cr-icon>
          </cr-flex>`
        : nothing}
      ${choices}
    </cr-flex> `;
  }
}
